import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useUpdateLocationAndHoursForShift } from "../../../NormalizedNurseMenu/useUpdateLocationAndHours";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { findStaffPlacement, type GapsScopedShiftGroup, type GapsStaffPlacement } from "../gapsGroupSheets";
import type { GapsHomeModel } from "../useGapsDayModel";
import { closeGapsSheetIfCurrent, GapsAssignUnitReason, type GapsSheetIntentOf, type GapsSheetKind } from "../useGapsSheet";
import { GapsSheetFrame } from "./GapsSheetFrame";
import { GapsUnitList } from "./GapsUnitList";
import { useSheetOpening } from "./useSheetOpening";

type Props = {
	/**
	 * What the sheet is up for, or `null` when it is not. Must be referentially stable while open (the store's intent
	 * object is): the placement lookup, the content kept for the exit animation and the sheet's own close key off it.
	 */
	target: GapsSheetIntentOf<GapsSheetKind.AssignUnit> | null;
	home: GapsHomeModel;
};

const SHEET = "gaps-assign-unit";

/** The heading per reason: an approve or an add has just happened, and the sheet says so before asking. */
const TITLE_KEY: Record<GapsAssignUnitReason, string> = {
	[GapsAssignUnitReason.StaffRow]: "gaps.assignUnit.title.staffRow",
	[GapsAssignUnitReason.Approve]: "gaps.assignUnit.title.approve",
	[GapsAssignUnitReason.Add]: "gaps.assignUnit.title.add",
};

/**
 * "Where does this person work?" — one sheet for all three ways a person lands on a shift without a unit (P3-6):
 * the staff row's "Assign unit", an approved pickup, a direct add. This part wires the staff row; the other two only
 * open it with their reason.
 *
 * The units are the group's own from the UNNARROWED day (`home.dayGroups`) — a pickup approved from Open Shifts can
 * land in a job the saved scope hides — emptiest first with their own `filled/required`. Tapping one saves it with
 * `updateLocationAndHours` directly: a location-only change, no overtime cascade (P3-5). "Assign later" leaves the
 * person on the shift without a unit, which is what the row's "Assign unit" link already says.
 */
export const GapsAssignUnitSheet: FC<Props> = ({ target, home }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const { updateLocationAndHours } = useUpdateLocationAndHoursForShift();
	const [savingLocation, setSavingLocation] = useState<string | null>(null);
	const { shown: opening } = useSheetOpening(target);
	// Closes THIS opening only: a save that finishes after the user moved on must not close what they moved to.
	const onClose = useCallback((): void => {
		if (opening !== null) closeGapsSheetIfCurrent(opening);
	}, [opening]);

	const live = useMemo(() => (target === null ? undefined : findStaffPlacement(home.dayGroups, target.shiftId)), [home.dayGroups, target]);
	// Kept past the close so the drawer animates out with its content rather than empty.
	const [retained, setRetained] = useState<{ placement: GapsStaffPlacement<GapsScopedShiftGroup>; reason: GapsAssignUnitReason } | undefined>(undefined);
	useEffect(() => {
		if (live !== undefined && target !== null) setRetained({ placement: live, reason: target.reason });
	}, [live, target]);
	// DEFAULT: the last placement found, for the exit animation only — a shift that leaves the day closes the sheet.
	const shown = live !== undefined && target !== null ? { placement: live, reason: target.reason } : retained;

	// A shift that left the day while the sheet was up has nowhere to be placed. Only once the day is loaded: an approval
	// from Open Shifts opens this sheet and THEN lands on the request's day, whose groups are empty until the week that
	// holds it is selected — "not found yet" there is not "gone".
	useEffect(() => {
		if (target !== null && live === undefined && home.isDayReady) onClose();
	}, [target, live, home.isDayReady, onClose]);

	const analyticsBase = useMemo(
		() =>
			shown === undefined ? {} : { department_id: chosenDepartment.department_id, shift_id: shown.placement.entry.slot.nurseInShift.id, reason: shown.reason },
		[chosenDepartment.department_id, shown],
	);

	const handleLater = useCallback((): void => {
		track("gaps_assign_unit_skipped", analyticsBase);
		onClose();
	}, [analyticsBase, onClose, track]);

	const handlePick = useCallback(
		async (locationName: string): Promise<void> => {
			if (shown === undefined) return;
			const { entry } = shown.placement;
			const shift = entry.slot.nurseInShift;
			if (locationName === entry.locationName) {
				onClose();
				return;
			}
			const properties = { ...analyticsBase, to: locationName };
			track("gaps_assign_unit_start", properties);
			setSavingLocation(locationName);
			const saved = await updateLocationAndHours({ originalNurseInShift: shift, updatedNurseInShift: { ...shift, location: locationName } });
			setSavingLocation(null);
			if (!saved) {
				// A changed location that did not save is a failed write, already in the mutation's error snackbar.
				trackError("gaps_assign_unit_error", properties);
				return;
			}
			track("gaps_assign_unit_success", properties);
			showSnackbar(t("gaps.assignUnit.saved", { name: entry.slot.name, unit: locationName }), "success");
			onClose();
		},
		[analyticsBase, onClose, showSnackbar, shown, t, track, trackError, updateLocationAndHours],
	);

	if (shown === undefined) return null;
	const { group, entry } = shown.placement;

	return (
		<GapsSheetFrame open={target !== null && live !== undefined} onClose={handleLater} dataTestId={`${SHEET}-sheet`}>
			<SheetStep
				stepKey={SHEET}
				dataTestId={SHEET}
				title={t(TITLE_KEY[shown.reason], { name: entry.slot.name })}
				subtitle={t("gaps.assignUnit.subtitle", { job: group.jobLabel, time: group.timeLabel })}
				footer={
					<SheetStepButton tone="dismiss" onClick={handleLater} dataTestId={`${SHEET}-cancel`}>
						{t("gaps.assignUnit.later")}
					</SheetStepButton>
				}
			>
				<GapsUnitList
					units={group.locations}
					currentLocation={entry.locationName}
					savingLocation={savingLocation}
					onPick={(locationName) => void handlePick(locationName)}
					rowTestIdPrefix={`${SHEET}-option`}
				/>
			</SheetStep>
		</GapsSheetFrame>
	);
};
